import React, { useMemo, useState } from 'react';
import {
  GraphMember,
  Side,
} from '@/contexts/FamilyGraphContext';
import type {
  CanonicalViewerMemberProjection,
} from '@/governor/tree/CanonicalTreeViewerProjection';
import { toast } from '@/components/ui/sonner';
import {
  Users,
  Eye,
  Link2,
  Trash2,
  Pencil,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  EyeOff,
  Filter,
} from 'lucide-react';
import MemberAvatar from './MemberAvatar';

/** Visible-tree filter currently active on the dashboard. Used to label
 *  why a roster row may not appear on the tree above. */
type RosterViewMode = 'full' | 'mother' | 'father' | 'extended';

interface Props {
  members: GraphMember[];
  canonicalViewerProjections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;
  /** IDs of relatives that cannot be drawn on the FULL tree as-is — they
   *  truly need a clearer relationship/side before placement is possible. */
  unplacedIds?: Set<string>;
  /** IDs of relatives that DO render on the full tree but are currently
   *  hidden because the active view chip filters them out. Empty in 'full'
   *  mode. Read-only — passing it never changes any data. */
  hiddenByFilterIds?: Set<string>;
  /** Active view chip on the dashboard (Full/Mom/Dad/Extended). */
  viewMode?: RosterViewMode;
  /** Called when the user wants to switch back to Full Tree from a "hidden
   *  by current view" badge. Pure UI — no DB write. */
  onClearViewFilter?: () => void;
  onView: (m: GraphMember) => void;
  onConnect: (m: GraphMember) => void;
  onDelete: (m: GraphMember) => Promise<void> | void;
}

/**
 * Determine the "bucket" a relative belongs to for the roster.
 * This deliberately uses the user's *declared* relationship + side; it does
 * not modify any data — it's purely a presentation grouping.
 */
const bucketFor = (m: GraphMember): {
  key: string;
  label: string;
  order: number;
  tone: string;
} => {
  const r = m.relationship;
  if (r === 'mother' || r === 'father' || r === 'step-parent' || r === 'adopted-parent') {
    return { key: 'parents', label: 'Parents', order: 1, tone: 'from-pink-500 to-rose-500' };
  }
  if (r === 'grandparent') {
    return { key: 'grandparents', label: 'Grandparents', order: 2, tone: 'from-fuchsia-500 to-purple-500' };
  }
  if (r === 'great-grandparent') {
    return { key: 'great', label: 'Great-Grandparents', order: 3, tone: 'from-purple-500 to-indigo-500' };
  }
  if (r === 'sibling') {
    return { key: 'siblings', label: 'Siblings', order: 4, tone: 'from-emerald-500 to-teal-500' };
  }
  if (r === 'spouse') {
    return { key: 'spouse', label: 'Spouse / Partner', order: 5, tone: 'from-rose-500 to-pink-500' };
  }
  if (r === 'aunt-uncle') {
    const sideLabel =
      m.side === 'mother' ? "Aunts & Uncles (Mom's side)" :
      m.side === 'father' ? "Aunts & Uncles (Dad's side)" :
      'Aunts & Uncles';
    const order = m.side === 'mother' ? 6 : m.side === 'father' ? 7 : 8;
    return { key: `auntuncle-${m.side}`, label: sideLabel, order, tone: 'from-amber-500 to-orange-500' };
  }
  if (r === 'cousin') {
    return { key: 'cousins', label: 'Cousins', order: 9, tone: 'from-orange-500 to-amber-500' };
  }
  if (r === 'cousin-child') {
    return { key: 'niblings', label: 'Nieces / Nephews / Cousin\'s children', order: 10, tone: 'from-yellow-500 to-orange-500' };
  }
  if (r === 'child') {
    return { key: 'children', label: 'Children', order: 11, tone: 'from-rose-500 to-pink-500' };
  }
  return { key: 'other', label: 'Other relatives', order: 99, tone: 'from-slate-400 to-slate-500' };
};

const sideBadge = (s: Side) =>
  s === 'mother' ? "Mom's side" :
  s === 'father' ? "Dad's side" :
  s === 'self' ? 'You' :
  'Shared';

const sideTone = (s: Side) =>
  s === 'mother' ? 'bg-pink-50 text-pink-700 border-pink-200' :
  s === 'father' ? 'bg-sky-50 text-sky-700 border-sky-200' :
  s === 'self' ? 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' :
  'bg-purple-50 text-purple-700 border-purple-200';

const RelativeRoster: React.FC<Props> = ({
  members,
  canonicalViewerProjections,
  unplacedIds,
  hiddenByFilterIds,
  viewMode = 'full',
  onClearViewFilter,
  onView,
  onConnect,
  onDelete,
}) => {
  type TabId = 'all' | 'unplaced' | 'hidden';
  const [tab, setTab] = useState<TabId>('all');
  const [expanded, setExpanded] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const visible = useMemo(() => {
    let list = members.filter(
      (m) =>
        m.approvalStatus !== 'rejected' &&
        // Hide the canonical "self" anchor — it's the YOU card on the tree.
        !(m.side === 'self' && m.relationship === 'unknown')
    );
    if (tab === 'unplaced' && unplacedIds) {
      list = list.filter((m) => unplacedIds.has(m.id));
    } else if (tab === 'hidden' && hiddenByFilterIds) {
      list = list.filter((m) => hiddenByFilterIds.has(m.id));
    }
    return list;
  }, [members, tab, unplacedIds, hiddenByFilterIds]);

  const groups = useMemo(() => {
    const map = new Map<string, { label: string; order: number; tone: string; rows: GraphMember[] }>();
    for (const m of visible) {
      const b = bucketFor(m);
      if (!map.has(b.key)) {
        map.set(b.key, { label: b.label, order: b.order, tone: b.tone, rows: [] });
      }
      map.get(b.key)!.rows.push(m);
    }
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => a.order - b.order);
  }, [visible]);

  const unplacedCount = unplacedIds?.size ?? 0;
  const hiddenCount = hiddenByFilterIds?.size ?? 0;
  const totalCount = members.filter(
    (m) =>
      m.approvalStatus !== 'rejected' &&
      !(m.side === 'self' && m.relationship === 'unknown')
  ).length;

  const handleDelete = async (
    member: GraphMember,
  ) => {
    setBusyId(member.id);

    try {
      await onDelete(member);
    } finally {
      setBusyId(null);
    }
  };

  if (totalCount === 0) return null;

  // Resolve a per-row status: visible / needs-connection / hidden-by-view.
  // Pure presentation — never modifies the underlying member.
  const statusFor = (m: GraphMember): 'visible' | 'needs' | 'hidden' => {
    if (unplacedIds?.has(m.id)) return 'needs';
    if (hiddenByFilterIds?.has(m.id)) return 'hidden';
    return 'visible';
  };

  const viewLabel =
    viewMode === 'mother' ? "Mom's Side" :
    viewMode === 'father' ? "Dad's Side" :
    viewMode === 'extended' ? 'Extended Relatives' :
    'Full Tree';

  return (
    <div className="rounded-2xl bg-white border-2 border-slate-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-5 py-4 flex items-center gap-3 bg-gradient-to-r from-slate-50 to-fuchsia-50/50 hover:from-slate-100 hover:to-fuchsia-100/50 transition"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white flex items-center justify-center shadow-sm">
          <Users className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <h3 className="font-serif font-bold text-lg text-slate-900 leading-tight">
            All your relatives
          </h3>
          <p className="text-xs text-slate-600">
            Every person you've added — visible whether or not they're placed on the tree above.
          </p>
        </div>
        <span className="text-xs font-bold text-fuchsia-700 bg-fuchsia-100 rounded-full px-2.5 py-1 mr-2">
          {totalCount}
        </span>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-500" />
        )}
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-1">
          {/* "Needs connection" note — the canonical roster ↔ graph
              affordance the spec calls for. Only shows when there really
              are relatives that the full tree cannot place. */}
          {unplacedCount > 0 && (
            <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] text-amber-900 flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">
                  Some relatives are listed below because they need a clearer connection.
                </div>
                <div className="text-amber-800/90">
                  Use <span className="font-semibold">Connect to Tree</span> to set their
                  relationship and side — we'll never move or merge anyone without your tap.
                </div>
              </div>
            </div>
          )}

          {/* "Hidden by current view" note — pure UX nudge that lets the
              user jump back to Full Tree to see them again. */}
          {viewMode !== 'full' && hiddenCount > 0 && (
            <div className="mb-3 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-800 flex items-start gap-2">
              <Filter className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">
                  {hiddenCount} {hiddenCount === 1 ? 'relative is' : 'relatives are'} hidden by the “{viewLabel}” filter.
                </div>
                <div className="text-slate-600">
                  They're still on your tree — switch to Full Tree to see everyone at once.
                </div>
              </div>
              {onClearViewFilter && (
                <button
                  type="button"
                  onClick={onClearViewFilter}
                  className="text-[11px] font-bold text-fuchsia-700 hover:bg-fuchsia-50 rounded-full px-2.5 py-1 border border-fuchsia-200 whitespace-nowrap"
                >
                  Show Full Tree
                </button>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setTab('all')}
              className={`text-xs font-bold rounded-full px-3 py-1.5 border transition ${
                tab === 'all'
                  ? 'bg-fuchsia-600 text-white border-fuchsia-600 shadow-sm'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              All relatives ({totalCount})
            </button>
            {unplacedCount > 0 && (
              <button
                type="button"
                onClick={() => setTab('unplaced')}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${
                  tab === 'unplaced'
                    ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                    : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                }`}
                title="Relatives that don't have a clear placement on the tree yet"
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                Needs connection ({unplacedCount})
              </button>
            )}
            {viewMode !== 'full' && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setTab('hidden')}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${
                  tab === 'hidden'
                    ? 'bg-slate-700 text-white border-slate-700 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
                title={`Relatives currently hidden by the “${viewLabel}” filter`}
              >
                <EyeOff className="w-3.5 h-3.5" />
                Hidden by view ({hiddenCount})
              </button>
            )}
          </div>

          {visible.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">
              {tab === 'unplaced'
                ? 'Every relative is placed on your tree. Nothing left to connect!'
                : tab === 'hidden'
                ? 'No relatives are hidden by the current view.'
                : 'No relatives yet — add your first family member above.'}
            </div>
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full bg-gradient-to-br ${g.tone}`}
                    />
                    <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-700">
                      {g.label}
                    </h4>
                    <span className="text-[10px] text-slate-500">({g.rows.length})</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {g.rows.map((m) => {
                      const status = statusFor(m);
                      const isPending = m.approvalStatus === 'pending';
                      const relLabel =
                        canonicalViewerProjections
                          .get(m.id)
                          ?.kinshipSemantics.primary.preciseLabel
                          ?.trim() ||
                        'Relationship unresolved';

                      const canonicalPath =
                        canonicalViewerProjections
                          .get(m.id)
                          ?.kinshipSemantics.primary.connectionPath ??
                        [];

                      const path =
                        canonicalPath.length === 0
                          ? null
                          : [
                              'You',
                              ...canonicalPath.map(
                                step =>
                                  members.find(
                                    candidate =>
                                      candidate.id ===
                                      step.toMemberId,
                                  )?.name ??
                                  'Family member',
                              ),
                            ];

                      const cardBorder =
                        status === 'needs'
                          ? 'border-amber-300 bg-amber-50/40'
                          : status === 'hidden'
                          ? 'border-slate-300 bg-slate-50/60'
                          : isPending
                          ? 'border-amber-200'
                          : 'border-emerald-200';
                      return (
                        <div
                          key={m.id}
                          className={`relative rounded-xl bg-white border p-3 shadow-sm hover:shadow transition flex items-start gap-3 ${cardBorder}`}
                        >
                          <MemberAvatar
                            name={m.name}
                            photoUrl={m.photoUrl}
                            size="md"
                            verified={m.approvalStatus === 'approved'}
                            pending={isPending}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 truncate text-sm">
                              {m.name}
                              {m.nickname ? (
                                <span className="text-slate-500 font-normal text-xs"> ({m.nickname})</span>
                              ) : null}
                            </div>
                            <div
                              className="text-[11px] text-slate-600 truncate"
                              title={path ? `Path: ${path.join(' → ')}` : undefined}
                            >
                              {relLabel}
                              {m.birthYear ? ` · b. ${m.birthYear}` : ''}
                            </div>
                            {path && path.length > 1 && (
                              <div
                                className="text-[10px] text-slate-500 truncate mt-0.5"
                                title={path.join(' → ')}
                              >
                                {path.join(' → ')}
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              <span
                                className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${sideTone(
                                  m.side
                                )}`}
                              >
                                {sideBadge(m.side)}
                              </span>
                              {isPending && (
                                <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border bg-amber-100 text-amber-800 border-amber-200">
                                  Pending
                                </span>
                              )}
                              {/* Tree-status indicator — exactly one of three. */}
                              {status === 'visible' && (
                                <span
                                  className="text-[10px] font-semibold rounded-full px-2 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1"
                                  title="This relative is currently drawn on the tree above."
                                >
                                  <CheckCircle2 className="w-2.5 h-2.5" />
                                  Visible on tree
                                </span>
                              )}
                              {status === 'needs' && (
                                <span
                                  className="text-[10px] font-semibold rounded-full px-2 py-0.5 border bg-amber-100 text-amber-800 border-amber-200 inline-flex items-center gap-1"
                                  title="This relative needs a clearer relationship or side before we can place them."
                                >
                                  <ShieldAlert className="w-2.5 h-2.5" />
                                  Needs connection
                                </span>
                              )}
                              {status === 'hidden' && (
                                <span
                                  className="text-[10px] font-semibold rounded-full px-2 py-0.5 border bg-slate-100 text-slate-700 border-slate-200 inline-flex items-center gap-1"
                                  title={`Hidden by the “${viewLabel}” filter — switch to Full Tree to see them.`}
                                >
                                  <EyeOff className="w-2.5 h-2.5" />
                                  Hidden by view
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => onView(m)}
                                className="text-[11px] font-semibold text-fuchsia-700 hover:bg-fuchsia-50 rounded-full px-2 py-1 inline-flex items-center gap-1 border border-fuchsia-200"
                                title="Open profile"
                              >
                                <Eye className="w-3 h-3" />
                                View
                              </button>
                              {/* "Connect to Tree" — promoted for relatives
                                  that truly need a clearer connection. For
                                  others it's still available as "Set". */}
                              {status === 'needs' ? (
                                <button
                                  type="button"
                                  onClick={() => onConnect(m)}
                                  className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-2.5 py-1 inline-flex items-center gap-1 border border-emerald-600 shadow-sm"
                                  title="Set this person's relationship & side so we can place them on the tree"
                                >
                                  <Link2 className="w-3 h-3" />
                                  Connect to Tree
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => onConnect(m)}
                                  className="text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 rounded-full px-2 py-1 inline-flex items-center gap-1 border border-emerald-200"
                                  title="Set or change this person's relationship & side"
                                >
                                  <Link2 className="w-3 h-3" />
                                  Connect
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => onConnect(m)}
                                className="text-[11px] font-semibold text-slate-700 hover:bg-slate-100 rounded-full px-2 py-1 inline-flex items-center gap-1 border border-slate-200"
                                title="Edit relationship"
                              >
                                <Pencil className="w-3 h-3" />
                                Set
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(m)}
                                disabled={busyId === m.id}
                                className="text-[11px] font-semibold text-slate-700 hover:bg-slate-100 rounded-full px-2 py-1 inline-flex items-center gap-1 border border-slate-200 disabled:opacity-50"
                                title="Review this record before any governed correction"
                              >
                                <Eye className="w-3 h-3" />
                                Review
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RelativeRoster;
